import { interval } from 'rxjs';
import { v4 as uuid } from 'uuid';
import Core from '../Core/Core';
import getLogger from '../Core/Logger';
import { ILogger } from '../Core/Logger';
import { app } from '../index';
import {
    AdContexts,
    AdGameAreas,
    AdParamBuilder,
    AdRewardTypes,
    IAdParams,
} from './AdParamBuilder';
import { IEventParams, IMetaParams } from './AllEventParams';
import { BotParamBuilder } from './BotParamBuilder';
import { CpParamBuilder } from './CpParamBuilder';
import { EpParamBuilder } from './EpParamBuilder';
import { IMissionParamsAll, IMissionParamsEnd, MissionParamBuilder } from './MissionParamBuilder';
import { ResourceParamBuilder } from './ResourceParamBuilder';
import { IParamsNewSaved, SavedParamBuilder } from './SavedParamBuilder';
import { IGameSessionParams, SessionParamBuilder } from './SessionParamBuilder';
import { SocialParamBuilder } from './SocialParamBuilder';
import { StateParamBuilder } from './StateParamBuilder';
import { TransactionParamBuilder } from './TransactionParamBuilder';
import { getLevelId } from '../State/reducers/level';
import { Platform } from '../SDK/SDK';

type IParamMap = { [key in DDNAEvents]: ParamGroups[] };

export enum ParamGroups {
    META,
    ENTRY_POINT,
    RESOURCE,
    MISSION,
    ADS,
    SOCIAL,
    BOTS,
    SAVED,
    SESSION,
    STATE,
    XPROMO,
    TRANSACTION,
}

const paramsNeeded: IParamMap = {
    newPlayer: [ParamGroups.META, ParamGroups.ENTRY_POINT],
    gameStarted: [ParamGroups.META, ParamGroups.ENTRY_POINT],
    clientDevice: [ParamGroups.META],
    ftux: [ParamGroups.META],
    transaction: [ParamGroups.META, ParamGroups.TRANSACTION],
    resource: [ParamGroups.META, ParamGroups.RESOURCE],
    missionStarted: [ParamGroups.META, ParamGroups.MISSION],
    missionFailed: [ParamGroups.META, ParamGroups.MISSION],
    missionCompleted: [ParamGroups.META, ParamGroups.MISSION],
    adRequest: [ParamGroups.META, ParamGroups.ADS],
    adShow: [ParamGroups.META, ParamGroups.ADS],
    socialAction: [ParamGroups.META, ParamGroups.SOCIAL],
    botOptionsChanged: [ParamGroups.META, ParamGroups.BOTS],
};

export enum DDNAEvents {
    NEW_PLAYER = 'newPlayer',
    GAME_STARTED = 'gameStarted',
    CLIENT_DEVICE = 'clientDevice',
    FTUX = 'ftux',
    TRANSACTION = 'transaction',
    RESOURCE = 'resource',
    MISSION_STARTED = 'missionStarted',
    MISSION_FAILED = 'missionFailed',
    MISSION_COMPLETED = 'missionCompleted',
    AD_REQUEST = 'adRequest',
    AD_SHOW = 'adShow',
    SOCIAL_ACTION = 'socialAction',
    BOT_OPTIONS_CHANGED = 'botOptionsChanged',
}

const logger: ILogger = getLogger('[DELTA_DNA]:');

const collectURL: string = 'https://collect15501mhjng.deltadna.net/collect/api/';
const devEnvKey: string = '47252375820130313129782600615622';
const envKey: string = '47252386056806303141486700615622';
const devUrl = collectURL + devEnvKey + '/bulk';
const deltaUrl = collectURL + envKey + '/bulk';

// these are not working due to CORS issues
// const validateUrl = 'https://www.deltadna.net/qa/47252375820130313129782600615622/bulk/';
// const mockUrl = 'https://www.deltadna.net/qa/collect/47252375820130313129782600615622/bulk';

interface ITrackGroupData<T>
    extends IAdParams,
        IMissionParamsAll,
        IMissionParamsEnd,
        IParamsNewSaved,
        IGameSessionParams,
        IMetaParams {}

export class Tracker {
    public readonly queueSaveKey: string = 'tracking-event-queue';

    public trackCurPropGetters: Map<
        ParamGroups,
        <T>(...args: Array<unknown>) => Partial<ITrackGroupData<T>>
    >;
    public trackCurPropSetters: Map<ParamGroups, (args: unknown) => void>;
    private trackQueue: any[] = [];
    private adBuilder: AdParamBuilder;
    private missionBuilder: MissionParamBuilder;
    private sessionBuilder: SessionParamBuilder;
    private stateBuilder: StateParamBuilder;
    private savedBuilder: SavedParamBuilder;
    private socialBuilder: SocialParamBuilder;
    private resourceBuilder: ResourceParamBuilder;
    private entryPointBuilder: EpParamBuilder;
    private crossPromoBuilder: CpParamBuilder;
    private botBuilder: BotParamBuilder;
    private transactionBuilder: TransactionParamBuilder;

    private trackGetters: Map<
        ParamGroups,
        <T>(...args: Array<unknown>) => Partial<ITrackGroupData<T>>
    >;
    private trackSetters: Map<ParamGroups, <T>(...args: Array<unknown>) => void>;

    constructor() {
        this.adBuilder = new AdParamBuilder();
        this.missionBuilder = new MissionParamBuilder();
        this.stateBuilder = new StateParamBuilder();
        this.sessionBuilder = new SessionParamBuilder();
        this.savedBuilder = new SavedParamBuilder();
        this.socialBuilder = new SocialParamBuilder();
        this.resourceBuilder = new ResourceParamBuilder();
        this.entryPointBuilder = new EpParamBuilder();
        this.crossPromoBuilder = new CpParamBuilder();
        this.botBuilder = new BotParamBuilder();
        this.transactionBuilder = new TransactionParamBuilder();

        // param getters (get all params of group);
        this.trackGetters = new Map<ParamGroups, <T>() => ITrackGroupData<T>>();
        this.trackGetters.set(ParamGroups.META, this.getMetaParams.bind(this));
        this.trackGetters.set(ParamGroups.ADS, this.adBuilder.getParams.bind(this.adBuilder));
        this.trackGetters.set(
            ParamGroups.MISSION,
            this.missionBuilder.getParams.bind(this.missionBuilder)
        );
        this.trackGetters.set(
            ParamGroups.SESSION,
            this.sessionBuilder.getParams.bind(this.sessionBuilder)
        );
        this.trackGetters.set(
            ParamGroups.STATE,
            this.stateBuilder.getParams.bind(this.stateBuilder)
        );
        this.trackGetters.set(
            ParamGroups.SAVED,
            this.savedBuilder.getParams.bind(this.savedBuilder)
        );
        this.trackGetters.set(
            ParamGroups.SOCIAL,
            this.socialBuilder.getParams.bind(this.socialBuilder)
        );
        this.trackGetters.set(
            ParamGroups.RESOURCE,
            this.resourceBuilder.getParams.bind(this.resourceBuilder)
        );
        this.trackGetters.set(
            ParamGroups.ENTRY_POINT,
            this.entryPointBuilder.getParams.bind(this.entryPointBuilder)
        );
        this.trackGetters.set(
            ParamGroups.XPROMO,
            this.crossPromoBuilder.getParams.bind(this.crossPromoBuilder)
        );
        this.trackGetters.set(ParamGroups.BOTS, this.botBuilder.getParams.bind(this.botBuilder));
        this.trackGetters.set(
            ParamGroups.TRANSACTION,
            this.transactionBuilder.getParams.bind(this.transactionBuilder)
        );

        // track setters (set specific params in group)
        this.trackSetters = new Map<ParamGroups, <T>(keys: Map<ParamGroups, unknown>) => void>();
        this.trackSetters.set(
            ParamGroups.MISSION,
            this.missionBuilder.setParams.bind(this.missionBuilder)
        );
        this.trackSetters.set(
            ParamGroups.SESSION,
            this.sessionBuilder.setParams.bind(this.sessionBuilder)
        );
        this.trackSetters.set(
            ParamGroups.SAVED,
            this.savedBuilder.setParams.bind(this.savedBuilder)
        );
        this.trackSetters.set(
            ParamGroups.SOCIAL,
            this.socialBuilder.setParams.bind(this.socialBuilder)
        );
        this.trackSetters.set(
            ParamGroups.RESOURCE,
            this.resourceBuilder.setParams.bind(this.resourceBuilder)
        );
        this.trackSetters.set(
            ParamGroups.ENTRY_POINT,
            this.entryPointBuilder.setParams.bind(this.entryPointBuilder)
        );
        this.trackSetters.set(
            ParamGroups.XPROMO,
            this.crossPromoBuilder.setParams.bind(this.crossPromoBuilder)
        );
        this.trackSetters.set(ParamGroups.BOTS, this.botBuilder.setParams.bind(this.botBuilder));
        this.trackSetters.set(
            ParamGroups.TRANSACTION,
            this.transactionBuilder.setParams.bind(this.transactionBuilder)
        );

        // track prop getters (get current set params);
        this.trackCurPropGetters = new Map<
            ParamGroups,
            <T>(...args: Array<unknown>) => Partial<ITrackGroupData<T>>
        >();
        this.trackCurPropGetters.set(
            ParamGroups.SESSION,
            this.sessionBuilder.getCurrentSessionData.bind(this.sessionBuilder)
        );
        this.trackCurPropGetters.set(
            ParamGroups.MISSION,
            this.missionBuilder.getCurrentMissionData.bind(this.missionBuilder)
        );
        this.trackCurPropGetters.set(
            ParamGroups.SAVED,
            this.savedBuilder.getCurrentSavedData.bind(this.savedBuilder)
        );

        this.trackCurPropSetters = new Map<ParamGroups, (args: unknown) => void>();
        this.trackCurPropSetters.set(
            ParamGroups.ADS,
            this.adBuilder.setParamsFromGame.bind(this.adBuilder)
        );
        this.trackCurPropSetters.set(
            ParamGroups.SAVED,
            this.savedBuilder.setParamsFromGame.bind(this.savedBuilder)
        );
        this.trackCurPropSetters.set(
            ParamGroups.SESSION,
            this.sessionBuilder.setParamsFromGame.bind(this.sessionBuilder)
        );
        this.trackCurPropSetters.set(
            ParamGroups.MISSION,
            this.missionBuilder.setParamsFromGame.bind(this.missionBuilder)
        );
        this.trackCurPropSetters.set(
            ParamGroups.ENTRY_POINT,
            this.entryPointBuilder.setParamsFromGame.bind(this.entryPointBuilder)
        );

        interval(10000).subscribe(() => {
            this.sendQueue();
        });
    }

    public async initSavedParams() {
        await this.savedBuilder.initSavedParams();
    }

    public initQueue(restoredQueue: Array<unknown>) {
        this.trackQueue = [...this.trackQueue, ...restoredQueue];
    }

    public addDirectParams(keys: Map<ParamGroups, unknown>) {
        keys.forEach((value, key) => {
            this.trackCurPropSetters.get(key)(value);
        });
    }

    public updateFromState(keys: Map<string, unknown>) {
        this.trackSetters.forEach(value => {
            value(keys);
        });
    }

    public addTrackedEvent(event: DDNAEvents, eventData?: unknown) {
        const eventDataGroups = paramsNeeded[event];
        const eventTrackData = eventDataGroups.reduce(
            (acc, group) => {
                acc = {
                    ...acc,
                    ...this.trackGetters.get(group)(event, eventData),
                };
                return acc;
            },
            {} as any
        );

        const uId = eventTrackData.userID;
        const sId = eventTrackData.sessionID;

        delete eventTrackData.eventName;
        delete eventTrackData.userID;
        delete eventTrackData.sessionID;
        // formatting....userid, sessionid need to be outside other params
        // all else sent as 'eventParams';
        // @ts-ignore
        const sendEvent = {
            eventName: event,
            userID: uId,
            sessionID: sId,
            eventUUID: uuid(),
            eventParams: eventTrackData,
        };

        logger.info(['Add to track queue:', event, sendEvent]);

        this.addToQueue(sendEvent);
    }

    public setSavedParams(data: any) {
        this.addDirectParams(new Map([[ParamGroups.SAVED, data]]));
    }

    public setSessionParams(data: any) {
        this.addDirectParams(new Map([[ParamGroups.SESSION, data]]));
    }

    public setEntryParams(data: any) {
        this.addDirectParams(new Map([[ParamGroups.ENTRY_POINT, data]]));
    }

    public setAdParams(
        type: AdRewardTypes,
        context: AdContexts,
        area: AdGameAreas,
        levelIndex: number
    ) {
        this.addDirectParams(
            new Map([
                [
                    ParamGroups.ADS,
                    {
                        adRewardType: type,
                        adContext: context,
                        adGameArea: area,
                        adMissionID: 0,
                        adLevelID: levelIndex > 0 ? getLevelId(levelIndex) : 'none',
                        adLevelNum: levelIndex > 0 ? levelIndex : 0,
                    },
                ],
            ])
        );
    }

    private addToQueue(event: unknown) {
        // @ts-ignore
        const dups = this.trackQueue.filter(ev => ev.eventUUID === event.eventUUID);
        if (dups.length === 0) {
            this.trackQueue.push(event);
            Core.sdk.data.saveData(this.queueSaveKey, this.trackQueue);
        }
    }

    private sendQueue() {
        if (
            sgPlatform === Platform.Local ||
            sgPlatform === Platform.Network ||
            this.trackQueue.length === 0
        ) {
            this.trackQueue = [];
            return;
        }

        const list = [...this.trackQueue];
        this.trackQueue = [];
        Core.sdk.data.saveData(this.queueSaveKey, this.trackQueue);

        const url = this.getTrackingUrl();
        const method = 'POST';
        const postData = JSON.stringify({ eventList: list });
        const request = new XMLHttpRequest();
        request.onload = res => {
            logger.info(['track queue sent:', postData, res]);
        };
        request.open(method, url, true);

        try {
            request.send(postData);
        } catch (err) {
            logger.warn([' queue error ', err]);
            this.trackQueue = [...this.trackQueue, ...list];
            Core.sdk.data.saveData(this.queueSaveKey, this.trackQueue);
        }
    }

    private getEventParams(event: DDNAEvents): IEventParams {
        return {
            eventName: event,
            timeSinceJSEnabled: Date.now() - this.sessionBuilder.sessionStart,
            frameRate: app.ticker.FPS || 0,
        };
    }

    private getMetaParams(event: DDNAEvents, passedData: unknown): IMetaParams {
        const d = { ...this.getEventParams(event) };
        return [ParamGroups.SESSION, ParamGroups.STATE, ParamGroups.SAVED].reduce(
            (acc, key) => {
                const a = {
                    ...acc,
                    ...this.trackGetters.get(key)(event, passedData),
                };
                return a;
            },
            d as IMetaParams
        );
    }

    private isDebugEnv() {
        return VERSION.includes('fbdev') || VERSION.includes('test');
    }

    private getTrackingUrl() {
        return this.isDebugEnv() ? devUrl : deltaUrl;
    }
}
